import { ArrowUpRight } from "lucide-react";
import { FadeUp, Overline, MaskedLines } from "../components/Reveal";
import Seo from "../components/Seo";
import { AWARDS, IMAGES } from "../data/content";

export default function Awards() {
  return (
    <main data-testid="awards-page" className="pt-40">
      <Seo
        title="Awards & Recognition — Gurpreet Paul"
        description="Gurpreet Paul's honors: Marquis Who's Who in America 2026, The Global Icon of Impact Award, CIO World Magazine's Iconic Founders & CEOs To Watch, Entrepreneur Top 150 New Franchises #47, and Franchise Voice Top 100 Influencers #55."
      />
      <header className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-20">
        <FadeUp>
          <Overline>Awards & Recognition</Overline>
        </FadeUp>
        <MaskedLines
          lines={["Recognized by", "the institutions", "that matter."]}
          className="font-display font-medium tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl text-paper mt-6"
          delay={0.15}
        />
      </header>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24" data-testid="awards-list">
        {AWARDS.map((award, i) => (
          <FadeUp key={i} delay={0.05}>
            <div
              data-testid={`award-item-${i + 1}`}
              className="group relative grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 py-12 border-t border-white/10 hover:border-brand/40 transition-colors duration-500"
            >
              <div className="lg:col-span-2">
                <span className="text-[12px] uppercase tracking-[0.25em] text-white/40 font-body group-hover:text-brand transition-colors duration-500">
                  {award.year}
                </span>
              </div>
              <div className="lg:col-span-7">
                <h2 className="font-display font-medium tracking-tight text-3xl md:text-4xl lg:text-5xl text-paper leading-tight group-hover:text-brand transition-colors duration-500">
                  {award.title}
                  {award.rank && <span className="text-brand ml-4">{award.rank}</span>}
                </h2>
                <p className="mt-5 text-white/50 text-base leading-relaxed font-body font-light max-w-2xl">
                  {award.detail}
                </p>
                {award.link && (
                  <a
                    href={award.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="award-press-link"
                    className="mt-6 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-white/40 hover:text-brand transition-colors duration-300 font-body"
                  >
                    {award.linkLabel} <ArrowUpRight size={14} />
                  </a>
                )}
              </div>
              <div className="lg:col-span-3 flex lg:justify-end items-start gap-8">
                {award.badge && (
                  <a
                    href={award.badgeLink || award.badge}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`award-badge-${i + 1}`}
                    className="shrink-0 hover:scale-105 transition-transform duration-300"
                    title={award.badgeLink ? "View the official listing (PDF)" : award.title}
                  >
                    <img
                      src={award.badge}
                      alt={`${award.title} — official badge`}
                      loading="lazy"
                      className={`${award.badgeClass || "h-24 lg:h-28"} w-auto object-contain`}
                    />
                  </a>
                )}
                <span className="font-body font-extralight tracking-widest text-5xl lg:text-6xl text-stroke leading-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
            </div>
          </FadeUp>
        ))}
      </section>

      <section data-testid="awards-marquis-feature" className="border-t border-white/10 bg-[#0D0D0F]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <FadeUp className="lg:col-span-7">
            <Overline>Marquis Who's Who · July 2026</Overline>
            <blockquote className="mt-6 font-display font-light tracking-tight text-3xl md:text-4xl leading-[1.2] text-paper">
              "Mr. Paul's journey exemplifies <span className="text-brand italic">dedication, innovation, and leadership</span> in both the business and community spheres."
            </blockquote>
            <p className="mt-8 text-white/50 text-base leading-relaxed font-body font-light max-w-2xl">
              Since 1899, Marquis Who's Who has chronicled the lives of the most accomplished individuals and
              innovators from every significant field of endeavor. Individuals profiled are selected on the basis
              of current reference value — position, noteworthy accomplishments, visibility, and prominence in a field.
            </p>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-5">
            <div className="overflow-hidden border border-white/10">
              <img
                src={IMAGES.portraitNavy}
                alt="Gurpreet Paul — Marquis Who's Who in America 2026 honoree, Founder and CEO of ThirsTea USA"
                loading="lazy"
                data-testid="awards-feature-image"
                className="w-full h-auto block hover:scale-[1.02] transition-transform duration-[1.2s]"
              />
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
